import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { TrashIcon, PencilIcon, PlusIcon, UserGroupIcon, ChatBubbleLeftIcon, DocumentIcon, CalendarIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import RaciAssignments from '../assignments/RaciAssignments';
import VideoPlayer from '../clientPortal/VideoPlayer';

// Mock data for when API is unavailable
const MOCK_STEP_DATA = {
  id: 'mock-step-1',
  name: 'Mock Step',
  description: 'This is mock step data displayed when the API is unavailable.',
  status: 'Not Started',
  phase_id: 'mock-phase-1',
  tasks: [
    {
      id: 'mock-task-1',
      name: 'Mock Task 1',
      description: 'This is a mock task',
      completed: false
    },
    {
      id: 'mock-task-2',
      name: 'Mock Task 2',
      description: 'This is another mock task',
      completed: true
    }
  ],
  resources: [
    {
      title: 'Mock Resource',
      url: 'https://example.com',
      description: 'A mock resource for reference'
    }
  ],
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
};

// VideoModal component
const VideoModal = ({ isOpen, onClose, videoUrl, title }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
        
        <div className="relative bg-white rounded-lg max-w-4xl w-full mx-auto shadow-xl overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium">{title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="w-full" style={{ height: "480px" }}>
            <VideoPlayer url={videoUrl} />
          </div>
          
          <div className="p-4 bg-gray-50 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              This video provides instructions and information about this step.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const StepDetails = () => {
  const [step, setStep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'notes', or 'assignments'
  
  // Video modal state
  const [showVideoModal, setShowVideoModal] = useState(false);
  
  // Edit form state
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStatus, setEditStatus] = useState('Not Started');
  const [editDueDate, setEditDueDate] = useState('');
  const [editVideoUrl, setEditVideoUrl] = useState('');
  
  // Notes state
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [isInternal, setIsInternal] = useState(true);
  const [notesFilter, setNotesFilter] = useState('all'); // 'all', 'internal', or 'external'
  const [isAddingNote, setIsAddingNote] = useState(false);
  
  const [availableStatuses, setAvailableStatuses] = useState([]);
  
  const { id } = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchStep = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Check if id is valid
        if (!id || id === 'undefined' || id === 'null' || id.includes('phase-')) {
          console.error('Invalid step ID:', id);
          setError({
            title: 'Invalid Step ID',
            message: 'This appears to be a template step which does not exist in the database. Template steps are stored as JSON data and cannot be accessed as individual database records.'
          });
          setLoading(false);
          return;
        }
        
        const response = await api.get(`/steps/${id}`);
        const stepData = response.data.step;
        setStep(stepData);
        
        // Initialize edit form values
        setEditName(stepData.name || '');
        setEditDescription(stepData.description || '');
        setEditStatus(stepData.status || 'Not Started');
        setEditDueDate(stepData.due_date ? new Date(stepData.due_date).toISOString().split('T')[0] : '');
        setEditVideoUrl(stepData.video_url || '');
        
        // Fetch notes for this step
        const notesResponse = await api.get(`/steps/${id}/notes`);
        if (notesResponse.data && notesResponse.data.notes) {
          setNotes(notesResponse.data.notes);
        }
      } catch (err) {
        console.error('Error fetching step details:', err);
        
        // Use mock data as fallback when API is unavailable
        if (err.message && err.message.includes('Network Error')) {
          console.log('Using mock step data due to network error');
          setStep(MOCK_STEP_DATA);
        } else {
          // Check for specific API error messages
          if (err.response && err.response.data && err.response.data.message) {
            setError({
              title: err.response.data.error || 'Error',
              message: err.response.data.message
            });
          } else {
            setError({
              title: 'Failed to load step details',
              message: 'Please try again later or contact support if the problem persists.'
            });
          }
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchStep();
  }, [id]);
  
  useEffect(() => {
    // Fetch available status types for steps
    const fetchStatusTypes = async () => {
      try {
        const response = await api.get('/settings/status-types/step');
        if (response.data && response.data.statusTypes) {
          setAvailableStatuses(response.data.statusTypes);
        }
      } catch (error) {
        console.error('Error fetching status types:', error);
      }
    };
    
    fetchStatusTypes();
  }, []);
  
  const handleStatusChange = async (newStatus) => {
    try {
      const response = await api.put(`/steps/${id}`, {
        status: newStatus
      });
      
      setStep(response.data);
    } catch (err) {
      console.error('Error updating step status:', err);
      
      // If network error, update local mock data
      if (err.message && err.message.includes('Network Error') && step) {
        setStep({
          ...step,
          status: newStatus
        });
        console.log('Updated mock data status due to network error');
      } else {
        toast.error('Failed to update status. Please try again.');
      }
    }
  };
  
  const handleAddNote = async (e) => {
    e.preventDefault();
    
    if (!newNote.trim()) {
      toast.error('Note content cannot be empty');
      return;
    }
    
    try {
      setIsAddingNote(true);
      
      const response = await api.post(`/steps/${id}/notes`, {
        content: newNote,
        is_internal: isInternal
      });
      
      // Add the new note to the list
      if (response.data && response.data.note) {
        setNotes([...notes, response.data.note]);
        setNewNote('');
        toast.success('Note added successfully');
      }
    } catch (err) {
      console.error('Error adding note:', err);
      toast.error('Failed to add note. Please try again.');
    } finally {
      setIsAddingNote(false);
    }
  };
  
  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await api.delete(`/steps/${id}`);
      setShowDeleteModal(false);
      navigate(`/phases/${step.phase_id}`);
    } catch (err) {
      console.error('Error deleting step:', err);
      toast.error('Failed to delete step. Please try again.');
      setIsDeleting(false);
    }
  };
  
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editName.trim()) {
      toast.error('Step name is required');
      return;
    }
    
    try {
      setIsEditing(true);
      
      const response = await api.put(`/steps/${id}`, {
        name: editName,
        description: editDescription,
        status: editStatus,
        due_date: editDueDate || null,
        video_url: editVideoUrl.trim() || null
      });
      
      setStep(response.data);
      setShowEditModal(false);
    } catch (err) {
      console.error('Error updating step:', err);
      toast.error('Failed to update step. Please try again.');
    } finally {
      setIsEditing(false);
    }
  };
  
  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Helper function to filter notes based on visibility selection
  const getFilteredNotes = () => {
    if (notesFilter === 'all') {
      return notes;
    }
    return notes.filter(note => 
      notesFilter === 'internal' ? note.is_internal : !note.is_internal
    );
  };
  
  // Open video modal
  const handleOpenVideo = () => {
    if (step && step.video_url) {
      setShowVideoModal(true);
    }
  };
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="w-12 h-12 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600">Loading step details...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-4 text-red-700 bg-red-100 rounded-md">
        <h3 className="font-medium">{error.title || 'Error'}</h3>
        <p className="mt-1">{error.message || 'An unexpected error occurred.'}</p>
        <div className="mt-4 flex space-x-4">
          <button 
            onClick={() => navigate('/projects')}
            className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Go to Projects
          </button>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm bg-red-700 text-white rounded-md hover:bg-red-800"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  if (!step) {
    return <div>Step not found</div>;
  }
  
  return (
    <div>
      <div className="mb-5 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{step.name}</h1>
          <p className="text-sm font-medium text-gray-500">
            <Link 
              to={`/phases/${step.phase_id}`} 
              className="text-blue-600 hover:text-blue-800"
            >
              Back to Phase
            </Link>
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <span 
            className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full items-center justify-center"
            style={{
              backgroundColor: availableStatuses.find(s => s.name === step.status)?.color || '#6B7280',
              color: 'white',
              minWidth: '80px',
              textAlign: 'center'
            }}
          >
            {step.status || 'Not Started'}
          </span>
          <div className="mt-6 flex space-x-3">
            <button
              onClick={() => setShowEditModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Edit
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              Delete
            </button>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex">
          <button
            onClick={() => setActiveTab('details')}
            className={`${
              activeTab === 'details'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm mr-8`}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab('notes')}
            className={`${
              activeTab === 'notes'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center mr-8`}
          >
            <ChatBubbleLeftIcon className="h-5 w-5 mr-1" />
            Notes
          </button>
          <button
            onClick={() => setActiveTab('assignments')}
            className={`${
              activeTab === 'assignments'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <UserGroupIcon className="h-5 w-5 mr-1" />
            Assignments
          </button>
        </nav>
      </div>
      
      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-6 mb-6">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Step Details
            </h3>
          </div>
          <div className="border-t border-gray-200">
            <dl>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Description
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {step.description || 'No description provided'}
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Status
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <div className="flex items-center space-x-2">
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium inline-flex items-center justify-center"
                      style={{
                        backgroundColor: availableStatuses.find(s => s.name === step.status)?.color || '#6B7280',
                        color: 'white',
                        minWidth: '80px',
                        textAlign: 'center'
                      }}
                    >
                      {step.status || 'Not Started'}
                    </span>
                    
                    <select
                      value={step.status || 'Not Started'}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    >
                      {availableStatuses.map((status) => (
                        <option key={status.id} value={status.name}>
                          {status.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Due Date
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 flex items-center">
                  <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                  {formatDate(step.due_date)}
                </dd>
              </div>
              {step.assigned_to && (
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Assigned To
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {step.assigned_to}
                  </dd>
                </div>
              )}
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Tutorial Video
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {step.video_url ? (
                    <div>
                      <button
                        onClick={handleOpenVideo}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <VideoCameraIcon className="h-4 w-4 mr-2" />
                        Watch Video Tutorial
                      </button>
                      <p className="mt-1 text-xs text-gray-500 truncate">
                        URL: {step.video_url}
                      </p>
                    </div>
                  ) : (
                    <span className="text-gray-500">No video tutorial available</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}
      
      {activeTab === 'notes' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-6 mb-6">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Notes
            </h3>
            <div className="flex space-x-2">
              <div className="relative inline-block text-left">
                <select
                  value={notesFilter}
                  onChange={(e) => setNotesFilter(e.target.value)}
                  className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <option value="all">All Notes</option>
                  <option value="internal">Internal Notes</option>
                  <option value="external">Client-Visible Notes</option>
                </select>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <div className="flow-root">
              <ul className="-mb-8">
                {getFilteredNotes().length > 0 ? (
                  getFilteredNotes().map((note, noteIdx) => (
                    <li key={note.id}>
                      <div className="relative pb-8">
                        {noteIdx !== getFilteredNotes().length - 1 ? (
                          <span className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                        ) : null}
                        <div className="relative flex items-start space-x-3">
                          <div className="relative">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${note.is_internal ? 'bg-blue-100' : 'bg-green-100'}`}>
                              <span className={`text-xs font-medium ${note.is_internal ? 'text-blue-800' : 'text-green-800'}`}>
                                {note.is_internal ? 'INT' : 'EXT'}
                              </span>
                            </div>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div>
                              <div className="text-sm">
                                <span className="font-medium text-gray-900">{note.created_by_name || 'System'}</span>
                              </div>
                              <p className="mt-0.5 text-sm text-gray-500">
                                {formatDate(note.created_at)}
                                {note.is_internal ? (
                                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    Internal
                                  </span>
                                ) : (
                                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Client-Visible
                                  </span>
                                )}
                              </p>
                            </div>
                            <div className="mt-2 text-sm text-gray-700">
                              <p className="whitespace-pre-wrap">{note.content}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No notes to display.</p>
                )}
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <h4 className="text-sm font-medium text-gray-500 mb-3">Add Note</h4>
            <div className="flex items-center mb-3">
              <span className="mr-4">Visible to:</span>
              <div className="flex items-center">
                <input
                  id="internal"
                  name="visibility"
                  type="radio"
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  checked={isInternal}
                  onChange={() => setIsInternal(true)}
                />
                <label htmlFor="internal" className="ml-2 block text-sm text-gray-900">
                  Team Only
                </label>
              </div>
              <div className="flex items-center ml-6">
                <input
                  id="external"
                  name="visibility"
                  type="radio"
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  checked={!isInternal}
                  onChange={() => setIsInternal(false)}
                />
                <label htmlFor="external" className="ml-2 block text-sm text-gray-900">
                  Client Visible
                </label>
              </div>
            </div>
            <div className="mb-4">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder={`Add a ${isInternal ? 'internal' : 'client-visible'} note...`}
              />
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleAddNote}
                disabled={!newNote.trim() || isAddingNote}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                  !newNote.trim() || isAddingNote ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isAddingNote ? 'Adding...' : 'Add Note'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'assignments' && (
        <div className="mt-6">
          <RaciAssignments entityType="step" entityId={step.id} />
        </div>
      )}
      
      {/* Video Modal */}
      {showVideoModal && step.video_url && (
        <VideoModal
          isOpen={showVideoModal}
          onClose={() => setShowVideoModal(false)}
          videoUrl={step.video_url}
          title={`Video Tutorial: ${step.name}`}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Step
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete this step? All associated tasks and resources will be permanently removed. This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleDelete}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm ${isDeleting ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Step Modal */}
      {showEditModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleEditSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                      <PencilIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Edit Step
                      </h3>
                      <div className="mt-2">
                        <div className="mb-4">
                          <label htmlFor="editName" className="block text-sm font-medium text-gray-700">
                            Step Name *
                          </label>
                          <input
                            type="text"
                            name="editName"
                            id="editName"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            placeholder="Enter step name"
                            required
                          />
                        </div>
                        <div className="mb-4">
                          <label htmlFor="editDescription" className="block text-sm font-medium text-gray-700">
                            Description
                          </label>
                          <textarea
                            id="editDescription"
                            name="editDescription"
                            rows={3}
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            placeholder="Enter step description"
                          />
                        </div>
                        <div className="mb-4">
                          <label htmlFor="editStatus" className="block text-sm font-medium text-gray-700">
                            Status
                          </label>
                          <select
                            id="editStatus"
                            name="editStatus"
                            value={editStatus}
                            onChange={(e) => setEditStatus(e.target.value)}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          >
                            <option value="">Select status</option>
                            {availableStatuses.map((status) => (
                              <option key={status.id} value={status.name}>
                                {status.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="mb-4">
                          <label htmlFor="editVideoUrl" className="block text-sm font-medium text-gray-700">
                            Video URL
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <VideoCameraIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                            </div>
                            <input
                              type="url"
                              name="editVideoUrl"
                              id="editVideoUrl"
                              value={editVideoUrl}
                              onChange={(e) => setEditVideoUrl(e.target.value)}
                              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                              placeholder="https://example.com/video"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            Enter a URL to a video tutorial for this step, or leave blank to remove
                          </p>
                        </div>
                        <div>
                          <label htmlFor="editDueDate" className="block text-sm font-medium text-gray-700">
                            Due Date
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <CalendarIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                            </div>
                            <input
                              type="date"
                              name="editDueDate"
                              id="editDueDate"
                              value={editDueDate}
                              onChange={(e) => setEditDueDate(e.target.value)}
                              className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                    disabled={isEditing}
                  >
                    {isEditing ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => setShowEditModal(false)}
                    disabled={isEditing}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StepDetails; 